import { useFormik } from 'formik'
import { useRouter } from 'next/router'
import { useEffect, useMemo } from 'react'
import { WorkspaceSettingsTab as View } from 'ui-components'
import { useAuth } from 'user-auth'
import { getUserLink, uploadFile } from 'utils'
import * as Yup from 'yup'
import { useDeleteWorkspaceById } from '~workspace/queries/useDeleteWorkspaceById'
import { useGetCurrentWorkspace } from '~workspace/queries/useGetWorkspaceById'
import { useGetWorkspaceLogoUploadUrl } from '~workspace/queries/useGetWorkspaceLogoUploadUrl'
import { useSearchMapsInCurrentWorkspace } from '~workspace/queries/useSearchMaps'
import {
  UpdateWorkspaceByIdVariables,
  useUpdateWorkspaceById
} from '~workspace/queries/useUpdateWorkspaceById'

const Settings = () => {
  const router = useRouter()

  const { user } = useAuth()
  const { data: workspace } = useGetCurrentWorkspace()
  const { data: searchMapsData } = useSearchMapsInCurrentWorkspace()
  const updateWorkspaceById = useUpdateWorkspaceById()
  const deleteWorkspaceById = useDeleteWorkspaceById()
  const getWorkspaceLogoUploadUrl = useGetWorkspaceLogoUploadUrl()

  const warningMessage = useMemo(() => {
    if (!user) return 'Unauthenticated'

    if (
      !!router.query.workspaceId &&
      !!user.defaultWorkspaceId &&
      router.query.workspaceId === user.defaultWorkspaceId
    ) {
      return WARNING_MESSAGES.DEFAULT_WORKSPACE
    }

    if (!!searchMapsData?.total && searchMapsData.total > 0) {
      return WARNING_MESSAGES.MAPS
    }

    return ''
  }, [router.query.workspaceId, user, searchMapsData])

  const form = useFormik({
    initialValues: {
      name: '',
      description: '',
      email: '',
      websiteUrl: '',
      communityUrl: '',
      twitterUsername: ''
    },
    initialStatus: {
      logo: ''
    },
    validationSchema: Yup.object().shape({
      name: Yup.string().max(50).required(),
      description: Yup.string().max(250),
      email: Yup.string().email('Must be a valid email'),
      websiteUrl: Yup.string().url('Must be a valid URL'),
      communityUrl: Yup.string().url('Must be a valid URL')
    }),
    onSubmit: async (values, helpers) => {
      const input: UpdateWorkspaceByIdVariables['input'] = values

      if (form.status?.logo && form.status.logo.startsWith('blob:')) {
        const fileBlob = await fetch(form.status.logo).then((r) => r.blob())

        const { uploadUrl, formFieldsJson, path } =
          await getWorkspaceLogoUploadUrl.mutateAsync({
            id: router.query.workspaceId!.toString()
          })

        await uploadFile({
          url: uploadUrl,
          formData: JSON.parse(formFieldsJson),
          file: new File([fileBlob], 'logo.png', { type: 'image/png' })
        })
        input.logo = path
      }

      await updateWorkspaceById.mutateAsync({
        id: router.query.workspaceId!.toString(),
        input
      })

      window.scrollTo({ top: 0, behavior: 'smooth' })
      helpers.resetForm()
    }
  })

  useEffect(() => {
    if (workspace) {
      form.setValues({
        name: workspace.name,
        description: workspace.description || '',
        email: workspace.email || '',
        websiteUrl: workspace.websiteUrl || '',
        communityUrl: workspace.communityUrl || '',
        twitterUsername: workspace.twitterUsername || ''
      })
      form.setStatus({
        logo: workspace.logo
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspace])

  const handleImageChange = async (file) => {
    form.setStatus({
      logo: URL.createObjectURL(file)
    })
  }

  const handleDeleteWorkspace = async () => {
    if (!user) return

    await deleteWorkspaceById.mutateAsync({
      id: router.query.workspaceId!.toString()
    })

    router.push(
      getUserLink({
        userId: user.id,
        firstName: user.firstName as string,
        lastName: user.lastName as string
      })
    )
  }

  return (
    <View
      form={form}
      warningMessage={warningMessage}
      deleteWorkspaceLoading={deleteWorkspaceById.isPending}
      onImageChange={handleImageChange}
      onDeleteWorkspace={handleDeleteWorkspace}
    />
  )
}

export { Settings }

const WARNING_MESSAGES = {
  DEFAULT_WORKSPACE: 'This is your default workspace, it cannot be deleted.',
  MAPS: 'You cannot delete a workspace that has maps. Please delete all maps first.'
}
